import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Autocomplete, Div, Dropdown } from '@pinpoint/design-kit';
import CONSTANTS from 'src/store/constants';
import { CellTag } from 'src/components/models/cells';

export function RolesDropdown({ defaultValue, onSelectRole, dropDownStyle, currentRole }) {
  const [isOpen, setIsOpen] = useState(false);
  const roles = CONSTANTS.ROLE_MAPPING[currentRole];

  return (
    <Dropdown
      h="2rem"
      isOpen={isOpen}
      onClick={() => setIsOpen((open) => !open)}
      menu={roles.map((role) => (
        <Div>{CONSTANTS.ROLE_DESC[CONSTANTS.ROLE_RENAMED[role] || role]}</Div>
      ))}
      onSelect={(index) => onSelectRole(roles[index])}
      defaultValue={CONSTANTS.ROLE_DESC[CONSTANTS.ROLE_RENAMED[defaultValue] || defaultValue] || defaultValue}
      {...dropDownStyle}
    />
  );
}

RolesDropdown.propTypes = {
  defaultValue: PropTypes.string,
  onSelectRole: PropTypes.func,
  dropDownStyle: PropTypes.shape({}),
  currentRole: PropTypes.string,
};

RolesDropdown.defaultProps = {
  defaultValue: 'Please select a role',
  onSelectRole: () => null,
  dropDownStyle: {},
  currentRole: 'VIEWER',
};

export function SiteRolesDropdown({ defaultValue, onSelectRole, dropDownStyle, currentRole }) {
  const [isOpen, setIsOpen] = useState(false);
  const roles = CONSTANTS.SITE_ROLE_MAPPING[currentRole] || [];

  return (
    <Dropdown
      h="2rem"
      isOpen={isOpen}
      onClick={() => setIsOpen((open) => !open)}
      menu={roles.map((role) => (
        <Div>{CONSTANTS.ROLE_DESC[CONSTANTS.ROLE_RENAMED[role] || role]}</Div>
      ))}
      onSelect={(index) => onSelectRole(roles[index])}
      defaultValue={CONSTANTS.ROLE_DESC[CONSTANTS.ROLE_RENAMED[defaultValue] || defaultValue] || defaultValue}
      {...dropDownStyle}
    />
  );
}

SiteRolesDropdown.propTypes = {
  defaultValue: PropTypes.string,
  onSelectRole: PropTypes.func,
  dropDownStyle: PropTypes.shape({}),
  currentRole: PropTypes.string,
};

SiteRolesDropdown.defaultProps = {
  defaultValue: 'Please select a role',
  onSelectRole: () => null,
  dropDownStyle: {},
  currentRole: CONSTANTS.ROLE.CLIENT_USER,
};

export function ModelsDropdown({ loading, models, model, onSelectModel, dropDownStyle, defaultText, valueIndices }) {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <Autocomplete
      name="model"
      w="400px"
      hoverBg="gray200"
      focusBg="gray200"
      bg="white"
      isOpen={isOpen}
      loading={loading}
      value={model ? model.name.replace(`${CONSTANTS.TAL_MODEL.TAG_NAME[model.dataModelType]}: `, '') : defaultText}
      valueIndices={valueIndices}
      onClick={() => setIsOpen(!isOpen)}
      menuOptions={{
        pos: 'absolute',
        shadow: 4,
        minW: '100%',
        rounded: 'md',
        maxH: '250px',
        bg: 'white',
        overflow: 'auto',
      }}
      menuItemOptions={{
        d: 'flex',
        align: 'center',
        p: { x: '1rem', y: '0.5rem' },
        hoverBg: 'gray100',
        bg: 'white',
      }}
      menu={models.map(({ name, dataModelType }) =>
        name.replace(`${CONSTANTS.TAL_MODEL.TAG_NAME[dataModelType]}: `, '')
      )}
      menuItemPrefix={(index) =>
        models[index]
          ? CellTag({
              value: {
                typeDetail: models[index]?.typeDetail,
                predictionType: models[index]?.predictionType,
                dataModelType: models[index]?.dataModelType,
                dataTest: `${models[index]?.dataModelType} ${models[index]?.name}`,
              },
              bg: 'white',
              m: { r: '.75rem' },
            })
          : null
      }
      onSelection={(index) => onSelectModel(models[index])}
      {...dropDownStyle}
    />
  );
}

ModelsDropdown.propTypes = {
  loading: PropTypes.bool.isRequired,
  models: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      dataModelType: PropTypes.string,
      typeDetail: PropTypes.string,
      predictionType: PropTypes.string,
    })
  ).isRequired,
  model: PropTypes.shape({
    name: PropTypes.string,
    dataModelType: PropTypes.string,
  }),
  onSelectModel: PropTypes.func.isRequired,
  dropDownStyle: PropTypes.shape({}),
  multiSelect: PropTypes.bool,
  valueIndices: PropTypes.arrayOf(PropTypes.number),
  defaultText: PropTypes.string,
};

ModelsDropdown.defaultProps = {
  dropDownStyle: {
    w: '100%',
  },
  multiSelect: false,
  defaultText: 'Please wait...',
  valueIndices: [],
  model: null,
};

export function DatasetsDropdown({
  loading,
  segments,
  segment,
  onSelectSegment,
  dropDownStyle,
  defaultText,
  valueIndices,
}) {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <Autocomplete
      w="400px"
      hoverBg="gray200"
      focusBg="gray200"
      bg="white"
      isOpen={isOpen}
      loading={loading}
      value={segment ? segment.dataset.name : defaultText}
      valueIndices={valueIndices}
      onClick={() => setIsOpen(!isOpen)}
      h="1.875rem"
      DropdownButtonOptions={{
        h: '1.875rem',
      }}
      inputStyle={{
        h: 'calc(1.875rem-1px)',
      }}
      menuOptions={{
        pos: 'absolute',
        shadow: 4,
        minW: '100%',
        rounded: 'md',
        maxH: '250px',
        bg: 'white',
        overflow: 'auto',
      }}
      menu={segments.map(({ dataset: { name } }) => name)}
      onSelection={(index) => {
        onSelectSegment(segments[index]);
      }}
      {...dropDownStyle}
    />
  );
}

DatasetsDropdown.propTypes = {
  loading: PropTypes.bool.isRequired,
  segments: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string })).isRequired,
  segment: PropTypes.shape({
    name: PropTypes.string,
    dataset: PropTypes.shape({
      name: PropTypes.string,
    }),
  }),
  onSelectSegment: PropTypes.func.isRequired,
  dropDownStyle: PropTypes.shape({}),
  multiSelect: PropTypes.bool,
  valueIndices: PropTypes.arrayOf(PropTypes.number),
  defaultText: PropTypes.string,
};

DatasetsDropdown.defaultProps = {
  dropDownStyle: {
    w: '100%',
  },
  multiSelect: false,
  defaultText: 'Please wait...',
  valueIndices: [],
  segment: null,
};

export function AgencyDropdown({
  loading,
  agencies,
  agency,
  onSelectAgency,
  dropDownStyle,
  defaultText,
  valueIndices,
}) {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <Autocomplete
      w="400px"
      hoverBg="gray200"
      focusBg="gray200"
      bg="white"
      isOpen={isOpen}
      loading={loading}
      value={agency ? agency.name : defaultText}
      valueIndices={valueIndices}
      onClick={() => setIsOpen(!isOpen)}
      menuOptions={{
        pos: 'absolute',
        shadow: 4,
        minW: '100%',
        rounded: 'md',
        maxH: '250px',
        bg: 'white',
        overflow: 'auto',
      }}
      menu={agencies.map(({ name }) => name)}
      onSelection={(index) => {
        onSelectAgency(agencies[index]);
      }}
      {...dropDownStyle}
    />
  );
}

AgencyDropdown.propTypes = {
  loading: PropTypes.bool.isRequired,
  agencies: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string })).isRequired,
  agency: PropTypes.shape({
    name: PropTypes.string,
  }),
  onSelectAgency: PropTypes.func.isRequired,
  dropDownStyle: PropTypes.shape({}),
  multiSelect: PropTypes.bool,
  valueIndices: PropTypes.arrayOf(PropTypes.number),
  defaultText: PropTypes.string,
};

AgencyDropdown.defaultProps = {
  dropDownStyle: {
    w: '100%',
  },
  multiSelect: false,
  defaultText: 'Please wait...',
  valueIndices: [],
  agency: null,
};
